import React, { useEffect } from 'react'
import { Button, Modal, Result } from 'antd'
import { useStore } from 'store'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { SESSION_RETURN_PATH_KEY } from 'constants/storage'

/**
 * Global modal that displays when a session expires (401 from API or Medplum).
 * Subscribes to the Zustand store's session.isExpired state and shows a blocking modal
 * prompting the user to refresh the page to re-login.
 *
 * This component prevents the error cascade to ErrorBoundary and Sentry by
 * intercepting token expiration at the fetch layer.
 *
 * The component must be rendered inside FeatureFlagProvider to access the
 * sessionExpiredModal feature flag.
 */
export function SessionExpiredModal(): JSX.Element | null {
  const isExpired = useStore((state) => state.session.isExpired)
  const { sessionExpiredModal: isFeatureEnabled } = useFeatureFlags()

  // Sync the feature flag to the store for non-React code
  useEffect(() => {
    useStore.getState().session.setFeatureEnabled(isFeatureEnabled)
  }, [isFeatureEnabled])

  // Store the current path in sessionStorage as a backup when session expires
  // (though the main capture happens in _app.tsx when redirecting to login)
  // Use window.location.pathname + search instead of asPath to ensure we get the actual URL
  // (asPath can sometimes be the route pattern with brackets instead of the actual path)
  useEffect(() => {
    if (isExpired && isFeatureEnabled && typeof window !== 'undefined') {
      const currentPath = window.location.pathname + window.location.search
      if (currentPath && currentPath !== '/login') {
        sessionStorage.setItem(SESSION_RETURN_PATH_KEY, currentPath)
      }
    }
  }, [isExpired, isFeatureEnabled])

  const handleRefresh = (): void => {
    window.location.reload()
  }

  if (!isFeatureEnabled) {
    return null
  }

  return (
    <Modal
      open={isExpired}
      closable={false}
      footer={null}
      maskClosable={false}
      centered
      width={480}
    >
      <Result
        status="warning"
        title="Session Expired"
        subTitle="Your session has expired. Please refresh the page to log in again."
        extra={
          <Button type="primary" onClick={handleRefresh}>
            Refresh
          </Button>
        }
      />
    </Modal>
  )
}

export default SessionExpiredModal
